import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Row, Col, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserCircle,
  faSignOutAlt,
  faUsers,
  faChartPie,
  faHeadset,
  faFileUpload,
  faLanguage,
  faWarehouseAlt,
  faAlignJustify,
  faBold,
  faSitemap,
  faBrowser,
  faRectangleHistoryCircleUser,
} from '@fortawesome/pro-duotone-svg-icons';
import { logout } from '../../actions/auth';
import Option from './Option';

function MainDash() {
  const {
    email,
    can_batch_upload: canBatch,
    can_view_shopper_data: canAgentRecords,
    can_view_users: canAdmin,
    can_view_reports: canReport,
    can_edit_translations: canTranslations,
    can_view_calls: canViewCalls,
    can_manage_agency: canManageAgencies,
    can_manage_campaigns: canManageCampaigns,
    can_manage_formulary: canManageFormulary,
    can_manage_logos: canManageLogos,
    can_manage_rts: canManageRTS,
    can_manage_leads: canManageLeads,
    report_access: reportAccess = [],
  } = useSelector(store => store.user);
  const {
    features: {
      analytics: analyticsFeature,
      agencies: agenciesFeature,
      campaigns: campaignsFeature,
      translations: translationsFeature,
      agent_records: agentRecordsFeature,
      logos: logosFeature,
      sites: sitesFeature,
      batch: batchFeature,
      rts: rtsFeature,
      leadsManagement: leadsFeature,
      disruptionReport: disruptionReportFeature,
    },
    disruptionRID,
    disruptionAWS,
    disruptionLabel,
    disruptionDescription,
  } = useSelector(store => store.theme);
  const dispatch = useDispatch();

  return (
    <main className="p-5">
      <Row>
        <Col lg="6">
          {canAdmin && (
            <>
              <h5 className="mb-3">
                <span>You are signed in as </span>
                <strong>{email}</strong>
                .
              </h5>
              <h5 className="mb-3">
                <Link to="/profile" onClick={() => dispatch({ type: 'CHANGE_TAB', tab: 'details' })}>
                  <FontAwesomeIcon icon={faUserCircle} size="lg" className="me-3 text-body" />
                  Manage your profile
                </Link>
              </h5>
            </>
          )}
          <h5 className="mb-3">
            <Button
              color="link"
              onClick={logout}
              className="h5 mb-0 p-0 align-bottom"
              style={{
                fontSize: '1.25rem',
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              <FontAwesomeIcon icon={faSignOutAlt} size="lg" className="me-3 text-body" />
              Sign out
            </Button>
          </h5>

          <hr className="bg-black my-4" />
        </Col>
      </Row>
      <Row>
        <Col>
          <h2 className="font-weight-bold mb-4">What do you want to accomplish today?</h2>
          <Row>
            {canAdmin && (
              <Option to="/admin" icon={faUsers} label="User Administration">
                Manage agent site access, permissions, licenses and more.
              </Option>
            )}
            {canManageLeads && leadsFeature && (
              <Option to="/leads" icon={faRectangleHistoryCircleUser} label="Manage Leads">
                Manage and reassign leads inventory
              </Option>
            )}
            {canReport && analyticsFeature && (
              <Option to="/analytics" icon={faChartPie} label="Analytics">
                View Business Intelligence reports to gain insights.
              </Option>
            )}
            {canAgentRecords && agentRecordsFeature && canViewCalls && (
              <Option to="/records" icon={faHeadset} label="Agent Records">
                Search and review agent call records.
              </Option>
            )}
            {canAdmin && canBatch && batchFeature && (
              <Option to="/batch" icon={faFileUpload} label="Batch Upload">
                Upload agent, license, and carrier permission batch files.
              </Option>
            )}
            {canAdmin && canTranslations && translationsFeature && (
              <Option to="/translations" icon={faLanguage} label="Translations">
                Configure multi-language support.
              </Option>
            )}
            {canAdmin && agenciesFeature && canManageAgencies && (
              <Option to="/agencies" icon={faWarehouseAlt} label="Agent Groups">
                Create and manage groups of agents for campaigns, reporting and user access.
              </Option>
            )}
            {canAdmin && canManageCampaigns && campaignsFeature && (
              <Option to="/campaigns" icon={faAlignJustify} label="Campaigns">
                Manage campaigns, including date ranges and associated agencies.
              </Option>
            )}
            {canAdmin && canManageFormulary && sitesFeature && (
              <Option to="/sites" icon={faBrowser} label="Site Management">
                Manage configurations for individual sites.
              </Option>
            )}
            {canAdmin && canManageLogos && logosFeature && (
              <Option to="/logos" icon={faBold} label="Logos & Display Names">
                Manage carrier logos and display names.
              </Option>
            )}
            {canAdmin && rtsFeature && canManageRTS && (
              <Option to="/rts" icon={faSitemap} label="RTS">
                View log data for RTS loads
              </Option>
            )}
            {disruptionReportFeature && Boolean((reportAccess || []).length) && (
              <Option
                to={`/analytics/qs/?rid=${disruptionRID}&aws=${disruptionAWS}&statePerms=1`}
                icon={faChartPie}
                label={disruptionDescription || disruptionLabel || 'Disruption Report'}
              >
                {disruptionLabel || 'Disruption Report'}
              </Option>
            )}
          </Row>
        </Col>
      </Row>
    </main>
  );
}

export default MainDash;
